'use client';

import { memo, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import TrailingLink from '@/components/atoms/TrailingLink';
import { usePathname } from 'next/navigation';
import type { MenuItem } from '@/types/navigation';
import styles from './SlideAccordion.module.scss';

export interface SlideAccordionProps {
  readonly items: ReadonlyArray<MenuItem>;
  readonly onLinkClick?: () => void;
}

export const SlideAccordion = memo(({ 
  items, 
  onLinkClick
}: SlideAccordionProps) => {
  const pathname = usePathname();
  const [activeItem, setActiveItem] = useState<MenuItem | null>(null);
  const [history, setHistory] = useState<MenuItem[]>([]);
  
  // Check if current item or its children are active
  const isActive = (item: MenuItem): boolean => {
    if (item.href && pathname === item.href) {
      return true;
    }
    if (item.children) {
      return item.children.some(child => isActive(child));
    }
    return false;
  };
  
  const handleItemClick = useCallback((item: MenuItem) => {
    if (item.children && item.children.length > 0) {
      setActiveItem(item);
      setHistory(prev => [...prev, item]);
    } else if (onLinkClick) {
      onLinkClick();
    }
  }, [onLinkClick]);
  
  const handleBack = useCallback(() => {
    const newHistory = [...history];
    newHistory.pop();
    setHistory(newHistory);
    
    if (newHistory.length === 0) {
      setActiveItem(null);
    } else {
      const lastItem = newHistory[newHistory.length - 1];
      if (lastItem) {
        setActiveItem(lastItem);
      }
    }
  }, [history]);
  
  // コンポーネント外にバリアントを抽出し再生成を防止
  const SLIDE_VARIANTS = {
    enter: { x: '100%', opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: '-100%', opacity: 0 },
  };

  const BACK_SLIDE_VARIANTS = {
    enter: { x: '-100%', opacity: 0 },
    center: { x: 0, opacity: 1 },
    exit: { x: '100%', opacity: 0 },
  };
 
  // スタイルタグマッピングを外部関数化
  const getStyleClass = (tag?: string): string => {
    switch (tag) {
      case 'fluorescent': return styles?.fluorescent ?? '';
      case 'fluorescent-blue': return styles?.fluorescentBlue ?? '';
      case 'fluorescent-indigo': return styles?.fluorescentIndigo ?? '';
      case 'menu-cyan': return styles?.menuCyan ?? '';
      case 'menu-blue': return styles?.menuBlue ?? '';
      case 'menu-cyan-blue': return styles?.menuCyanBlue ?? '';
      case 'menu-blue-indigo': return styles?.menuBlueIndigo ?? '';
      case 'menu-indigo': return styles?.menuIndigo ?? '';
      case 'menu-gradient-sky-light': return styles?.menuGradientSkyLight ?? '';
      case 'menu-gradient-light-blue': return styles?.menuGradientLightBlue ?? '';
      default: return '';
    }
  };

  // Map depth level to CSS class
  // Visual level logic: main menu = level0, first submenu = level1, second submenu = level2
  const getLevelClass = () => {
    const currentLevel = activeItem === null ? 0 : history.length;
    switch (currentLevel) {
      case 0:
        return styles.level0;
      case 1:
        return styles.level1;
      case 2:
        return styles.level2;
      case 3:
      default:
        return styles.level3;
    }
  };

  // Identify if item is special back link
  const getArrowIcon = (id: string): 'single' | 'double' | null => {
    if (id.endsWith('-top')) {
      return 'double'; // third level back to menu top
    }
    if (id.endsWith('-header') || id.endsWith('-nav-left')) {
      return 'single'; // second/third level back
    }
    return null;
  };

  return (
    <div className={`${styles.container} ${getLevelClass()}`}>
      <AnimatePresence mode="wait">
        {activeItem === null ? (
          <motion.div
            key="main-menu"
            className={`${styles.menuLevel} ${getLevelClass()}`}
            variants={history.length > 0 ? BACK_SLIDE_VARIANTS : SLIDE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'tween', duration: 0.3 }}
          >
            {/* メニュータイトル */}
            <div className={styles.menuTitle}>
              <span className={styles.menuTitleText}>メインメニュー</span>
            </div>
            
            {/* Direct access link for level 0 - ホーム */}
            <div className={styles.levelDirectAccess}>
              <TrailingLink
                href="/"
                onClick={onLinkClick}
                className={`${styles.levelDirectLink} ${styles.level0DirectLink} ${pathname === '/' ? styles.isCurrent : ''}`}
              >
                <span className={styles.menuText}>ホーム</span>
              </TrailingLink>
            </div>
            
            {items.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const styleClass = getStyleClass(item.styleTag);
              const labelOnly = !item.href;
              const itemIsActive = isActive(item);
              
              return (
                <div key={item.id} className={styles.menuItem}>
                  {labelOnly ? (
                    // Independent guide text (centered)
                    <div className={`${styles.guideText} ${styleClass}`}>{item.label}</div>
                  ) : hasChildren ? (
                    <button
                      onClick={() => handleItemClick(item)}
                      className={`${styles.menuButton} ${styles.level0Button} ${styleClass} ${itemIsActive ? styles.isCurrent : ''}`}
                      aria-expanded={false}
                    >
                      <span className={styles.menuText}>{item.label}</span>
                      <span className={styles.arrow}>
                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <path 
                            d="M6 12l4-4-4-4" 
                            stroke="currentColor" 
                            strokeWidth="2" 
                            strokeLinecap="round" 
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    </button>
                  ) : (
                    <TrailingLink
                      href={item.href}
                      onClick={onLinkClick}
                      className={`${styles.menuLink} ${styles.level0Button} ${styleClass} ${item.href && pathname === item.href ? styles.isCurrent : ''}`}
                    >
                      {(() => {
                        const arrow = getArrowIcon(item.id);
                        if (arrow === 'single') {
                          return (
                            <>
                              <span className={styles.leftArrow}>
                                <svg viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </span>
                              {item.label}
                            </>
                          );
                        }
                        if (arrow === 'double') {
                          return (
                            <>
                              <span className={styles.doubleLeftArrow}>
                                <svg viewBox="0 0 16 16" fill="none"><path d="M11 12L7 8l4-4M7 12L3 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </span>
                              {item.label}
                            </>
                          );
                        }
                        return item.label;
                      })()}
                    </TrailingLink>
                  )}
                </div>
              );
            })}
          </motion.div>
        ) : (
          <motion.div
            key={`submenu-${activeItem.id}`}
            className={`${styles.menuLevel} ${getLevelClass()}`}
            variants={SLIDE_VARIANTS}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ type: 'tween', duration: 0.3 }}
          >
            {/* Back buttons are now handled as menu items below */}
            
            {/* Menu subtitle for all levels */}
            {/* Determine depth: first submenu (level1) -> history.length === 1 */}
            <div className={`${styles.menuSubtitle} ${history.length === 1 ? styles.level1Subtitle : history.length === 2 ? styles.level2Subtitle : styles.level3Subtitle}`}>
              <span className={`${styles.menuSubtitleText} ${history.length === 1 ? styles.level1Text : history.length === 2 ? styles.level2Text : styles.level3Text}`}>
                {activeItem.label}のメニュー
              </span>
            </div>
            
            {/* Direct access link for all levels with href */}
            {activeItem.href && (
              <div className={styles.levelDirectAccess}>
                <TrailingLink
                  href={activeItem.href}
                  onClick={onLinkClick}
                  className={`${styles.levelDirectLink} ${history.length === 1 ? styles.level1DirectLink : history.length === 2 ? styles.level2DirectLink : styles.level3DirectLink} ${pathname === activeItem.href ? styles.isCurrent : ''}`}
                >
                  <span className={styles.menuText}>{activeItem.label}</span>
                </TrailingLink>
              </div>
            )}
            
            {activeItem.children && (
              <div className={styles.submenu}>
                {activeItem.children.map((child) => (
                  <div key={child.id} className={styles.menuItem}>
                    {(!child.href) ? (
                      <div className={`${styles.guideText} ${getStyleClass(child.styleTag)}`}>{child.label}</div>
                    ) : (child.styleTag && (child.styleTag === 'menu-cyan' || child.styleTag === 'menu-blue')) ? (
                      (() => {
                        // Check if this is a navigation item that should have arrows
                        const isMenuTop = child.id.endsWith('-menu-top');
                        const isParentSection = child.id.endsWith('-nav-left');
                        
                        if (isMenuTop || isParentSection) {
                          return (
                            <button
                              onClick={() => {
                                if (isMenuTop) {
                                  // Reset to main menu
                                  setActiveItem(null);
                                  setHistory([]);
                                } else {
                                  // Go back one level
                                  handleBack();
                                }
                              }}
                              className={`${styles.menuButton} ${getStyleClass(child.styleTag)}`}
                            >
                              {(isMenuTop || isParentSection) && (
                                <span className={styles.leftArrow}>
                                  <svg viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                </span>
                              )}
                              <span className={styles.menuText}>{child.label}</span>
                            </button>
                          );
                        }
                        
                        // Regular styled link
                        return (
                          <TrailingLink href={child.href} className={`${styles.menuLink} ${getStyleClass(child.styleTag)}`}>
                            <span className={styles.menuText}>{child.label}</span>
                          </TrailingLink>
                        );
                      })()
                    ) : (child.children && child.children.length > 0) ? (
                      <button
                        onClick={() => handleItemClick(child)}
                        className={`${styles.menuButton} ${history.length === 1 ? styles.level1Button : history.length === 2 ? styles.level2Button : styles.level3Button} ${child.styleTag ? getStyleClass(child.styleTag) : ''} ${isActive(child) ? styles.isCurrent : ''}`}
                        aria-expanded={false}
                      >
                        <span className={styles.menuText}>{child.label}</span>
                        <span className={styles.arrow}>
                          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path 
                              d="M6 12l4-4-4-4" 
                              stroke="white" 
                              strokeWidth="2" 
                              strokeLinecap="round" 
                              strokeLinejoin="round"
                            />
                          </svg>
                        </span>
                      </button>
                    ) : (
                      (() => {
                        // Special handling for back navigation items
                        if (child.id.endsWith('-menu-top')) {
                          // Menu top link - navigates to home and resets menu
                          // history.length === 0 (第一階層): leftArrow
                          // history.length === 1 (第二階層): doubleLeftArrow
                          return (
                            <button
                              onClick={() => {
                                setActiveItem(null);
                                setHistory([]);
                              }}
                              className={`${styles.menuButton} ${getStyleClass(child.styleTag)}`}
                            >
                              {/* First submenu (depth 1) uses single left arrow; deeper uses double */}
                              <span className={history.length === 1 ? styles.leftArrow : styles.doubleLeftArrow}>
                                {history.length === 1 ? (
                                  <svg viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                ) : (
                                  <svg viewBox="0 0 16 16" fill="none"><path d="M11 12L7 8l4-4M7 12L3 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                                )}
                              </span>
                              <span className={styles.menuText}>{child.label}</span>
                            </button>
                          );
                        }
                        if (child.id.endsWith('-nav-left')) {
                          // Parent section link - goes back one level
                          // Always uses leftArrow (single arrow) regardless of current level
                          return (
                            <button
                              onClick={handleBack}
                              className={`${styles.menuButton} ${getStyleClass(child.styleTag)}`}
                            >
                              <span className={styles.leftArrow}>
                                <svg viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
                              </span>
                              <span className={styles.menuText}>{child.label}</span>
                            </button>
                          );
                        }
                        // Regular link item
                        return (
                          <TrailingLink
                            href={child.href}
                            onClick={onLinkClick}
                            className={`${styles.menuLink} ${history.length === 1 ? styles.level1Button : history.length === 2 ? styles.level2Button : styles.level3Button} ${child.styleTag ? getStyleClass(child.styleTag) : ''} ${pathname === child.href ? styles.isCurrent : ''}`}
                          >
                            <span className={styles.menuText}>{child.label}</span>
                          </TrailingLink>
                        );
                      })()
                    )}
                  </div>
                ))}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

SlideAccordion.displayName = 'SlideAccordion';

export default SlideAccordion;